import cx from 'classnames';
import React, { MouseEventHandler, ReactNode, useMemo } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { FilterCounter } from '@shared/components/filter-counter';
import { Tooltip } from '@shared/components/tooltip';

import { styles } from './Badge.styles';

export enum BadgeTheme {
  blue = 'blue',
  danger = 'danger',
  deepBlue = 'deepBlue', // TODO: Change after palette updated
  empty = 'empty',
  green = 'green',
  grey = 'grey',
  navy = 'navy',
  orange = 'orange', // TODO: Change after palette updated
  purple = 'purple',
  teal = 'teal',
  warning = 'warning',
  white = 'white',
  app = 'app',
}

export enum BadgeSize {
  small = 'small',
  large = 'large',
}

export interface BadgeProps extends WithStyles<typeof styles> {
  content: ReactNode;
  count?: number;
  size?: BadgeSize;
  startIcon?: ReactNode;
  theme?: BadgeTheme;
  tooltip?: ReactNode;
  tooltipDelay?: number;
  onClick?: MouseEventHandler;
}

const BadgeComponent: React.FC<BadgeProps> = ({
  classes,
  content,
  count,
  size = BadgeSize.small,
  startIcon,
  theme = BadgeTheme.grey,
  tooltip,
  tooltipDelay,
  onClick,
}) => {
  const badgeContent = useMemo(() => {
    return (
      <>
        {startIcon}
        {content}
        {typeof count === 'number' && (
          <FilterCounter
            classes={{
              root: classes.counterRoot,
              rootZero: classes.counterRootZero,
              count: classes.counterCount,
              countZero: classes.counterCountZero,
            }}
            count={count}
          />
        )}
      </>
    );
  }, [classes, content, count, startIcon]);

  if (tooltip) {
    return (
      <Tooltip
        classes={{ container: cx(classes.badge, classes[size], classes[theme]), root: classes.root }}
        enterDelay={tooltipDelay}
        enterNextDelay={tooltipDelay}
        placement="top"
        title={tooltip}
        onClick={onClick}
      >
        {badgeContent}
      </Tooltip>
    );
  }

  return (
    <div className={cx(classes.root, classes.badge, classes[size], classes[theme])} onClick={onClick}>
      {badgeContent}
    </div>
  );
};

export const Badge = withStyles(styles)(BadgeComponent);
